import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Grid,
  FormHelperText,
  CircularProgress,
  ListSubheader,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Chip,
  Box,
} from '@material-ui/core';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import DeleteIcon from '@material-ui/icons/Delete';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import { Query, Mutation } from 'react-apollo';
import gql from 'graphql-tag';
import { withSnackbar } from 'notistack';
import Autocomplete from '@material-ui/lab/Autocomplete';
import InventoryUserAutocomplete from '../common/InventoryUserAutocomplete';

// GraphQL Queries
const GET_FORM_DATA = gql`
  query GetQuickNotesFormData {
    getActivityTypes {
      id
      name
    }
    getQuickNoteCategories {
      id
      name
      description
    }
  }
`;

const GET_ACTIVE_DEALS = gql`
  query GetActiveDealsForQuickNotes {
    active_deals_for_quick_notes {
      id
      name
      pipeline {
        id
        name
      }
      pipeline_stage {
        id
        name
      }
    }
  }
`;

const CREATE_QUICK_NOTE = gql`
  mutation CreateQuickNote(
    $inventory_user_id: ID!
    $comment: String!
    $time_spent: Int!
    $activity_type_id: ID!
    $category_ids: [ID!]
    $interaction_date: String!
    $planned: Boolean!
    $attachments: [File!]
    $pipeline_card_id: ID
  ) {
    createQuickNote(
      inventory_user_id: $inventory_user_id
      comment: $comment
      time_spent: $time_spent
      activity_type_id: $activity_type_id
      category_ids: $category_ids
      interaction_date: $interaction_date
      planned: $planned
      attachments: $attachments
      pipeline_card_id: $pipeline_card_id
    ) {
      id
      comment
      time_spent
      time_spent_formatted
      activity_type_id
      quick_note_category_id
      interaction_date
      planned
      created_at
      pipeline_card_id
    }
  }
`;

const CREATE_QUICK_NOTES_BULK = gql`
  mutation CreateQuickNotesBulk(
    $inventory_user_ids: [ID!]!
    $comment: String!
    $time_spent: Int!
    $activity_type_id: ID!
    $category_ids: [ID!]
    $interaction_date: String!
    $planned: Boolean!
    $attachments: [File!]
    $pipeline_card_id: ID
  ) {
    createQuickNotesBulk(
      inventory_user_ids: $inventory_user_ids
      comment: $comment
      time_spent: $time_spent
      activity_type_id: $activity_type_id
      category_ids: $category_ids
      interaction_date: $interaction_date
      planned: $planned
      attachments: $attachments
      pipeline_card_id: $pipeline_card_id
    ) {
      notes {
        id
        comment
        time_spent
        time_spent_formatted
        activity_type_id
        quick_note_category_id
        interaction_date
        planned
        created_at
        pipeline_card_id
      }
      count
      errors
    }
  }
`;

const ENQUEUE_QUICK_NOTES_BULK = gql`
  mutation EnqueueQuickNotesBulk(
    $inventory_user_ids: [ID!]!
    $comment: String!
    $time_spent: Int!
    $activity_type_id: ID!
    $category_ids: [ID!]
    $interaction_date: String!
    $planned: Boolean!
    $attachments: [File!]
    $pipeline_card_id: ID
  ) {
    enqueueQuickNotesBulk(
      inventory_user_ids: $inventory_user_ids
      comment: $comment
      time_spent: $time_spent
      activity_type_id: $activity_type_id
      category_ids: $category_ids
      interaction_date: $interaction_date
      planned: $planned
      attachments: $attachments
      pipeline_card_id: $pipeline_card_id
    ) {
      count
      message
      errors
    }
  }
`;

// Time spent options (15-minute increments from 15 minutes to 8 hours)
const TIME_SPENT_OPTIONS = [];
for (let i = 15; i <= 480; i += 15) {
  const hours = Math.floor(i / 60);
  const minutes = i % 60;
  let label;
  if (hours === 0) {
    label = `${minutes} minutes`;
  } else if (minutes === 0) {
    label = `${hours} hour${hours > 1 ? 's' : ''}`;
  } else {
    label = `${hours} hour${hours > 1 ? 's' : ''} ${minutes} minutes`;
  }
  TIME_SPENT_OPTIONS.push({ value: i, label });
}

class QuickNotesForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedUsers: [], // Changed to support multiple users
      comment: '',
      timeSpent: 30,
      activityTypeId: '',
      categoryIds: [], // Changed from categoryId to categoryIds (array)
      interactionDate: new Date(),
      planned: false,
      attachments: [], // File attachments
      selectedPipelineCard: null, // Link to case/deal
      errors: {},
      isSubmitting: false
    };
  }

  validateForm = () => {
    const errors = {};
    const { selectedUsers, comment, timeSpent, activityTypeId, categoryIds, interactionDate, planned } = this.state;

    // User validation - bulk mode only
    if (!selectedUsers || selectedUsers.length === 0) {
      errors.selectedUsers = 'Please select at least 1 user';
    } else if (selectedUsers.length > 10) {
      errors.selectedUsers = 'Maximum 10 users allowed for bulk creation';
    } else if (selectedUsers.some(user => !user.id)) {
      errors.selectedUsers = 'Invalid user selection detected';
    }

    // Comment validation
    if (!comment.trim()) {
      errors.comment = 'Comment is required';
    } else if (comment.trim().length < 5) {
      errors.comment = 'Comment must be at least 5 characters long';
    } else if (comment.trim().length > 2000) {
      errors.comment = 'Comment cannot exceed 2000 characters';
    }

    // Time spent validation
    if (!timeSpent) {
      errors.timeSpent = 'Time spent is required';
    } else {
      const timeValue = parseInt(timeSpent);
      if (isNaN(timeValue) || timeValue < 15 || timeValue > 480) {
        errors.timeSpent = 'Time spent must be between 15 minutes and 8 hours';
      } else if (timeValue % 15 !== 0) {
        errors.timeSpent = 'Time spent must be in 15-minute increments';
      }
    }

    // Activity type validation
    if (!activityTypeId) {
      errors.activityTypeId = 'Activity type is required';
    } else if (typeof activityTypeId !== 'string' && typeof activityTypeId !== 'number') {
      errors.activityTypeId = 'Invalid activity type selection';
    }

    // Category validation
    if (!categoryIds || categoryIds.length === 0) {
      errors.categoryIds = 'At least one category is required';
    } else if (!Array.isArray(categoryIds)) {
      errors.categoryIds = 'Invalid category selection';
    }

    // Date validation
    if (!interactionDate) {
      errors.interactionDate = 'Interaction date is required';
    } else {
      const today = new Date();
      today.setHours(23, 59, 59, 999); // End of today
      
      if (interactionDate > today) {
        errors.interactionDate = 'Interaction date cannot be in the future';
      }
      
      // Check if date is too far in the past (more than 1 year)
      const oneYearAgo = new Date();
      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
      if (interactionDate < oneYearAgo) {
        errors.interactionDate = 'Interaction date cannot be more than 1 year ago';
      }
      
      // Check if it's a valid date
      if (isNaN(interactionDate.getTime())) {
        errors.interactionDate = 'Please enter a valid date';
      }
    }

    // Planned validation
    if (typeof planned !== 'boolean') {
      errors.planned = 'Please select planned or unplanned';
    }

    this.setState({ errors });
    return Object.keys(errors).length === 0;
  };

  // Add real-time validation for comment length
  handleCommentChange = (event) => {
    const comment = event.target.value;
    const errors = { ...this.state.errors };
    
    // Clear comment error if it exists and comment is now valid
    if (errors.comment && comment.trim().length >= 5 && comment.trim().length <= 2000) {
      delete errors.comment;
    }
    
    this.setState({
      comment,
      errors,
    });
  };

  // Enhanced error handling for GraphQL errors
  handleGraphQLError = (error) => {
    const { enqueueSnackbar } = this.props;
    
    if (error.graphQLErrors && error.graphQLErrors.length > 0) {
      // Handle specific GraphQL errors
      const graphQLError = error.graphQLErrors[0];
      const message = graphQLError.message;
      
      // Check for specific validation errors from the server
      if (message.includes('InventoryUser not found')) {
        this.setState({
          errors: { ...this.state.errors, selectedUser: 'Selected user not found' }
        });
        enqueueSnackbar('Selected user not found. Please select a different user.', { variant: 'error' });
      } else if (message.includes('Activity type')) {
        this.setState({
          errors: { ...this.state.errors, activityTypeId: 'Invalid activity type' }
        });
        enqueueSnackbar('Invalid activity type. Please select a valid activity type.', { variant: 'error' });
      } else if (message.includes('Category')) {
        this.setState({
          errors: { ...this.state.errors, categoryIds: 'Invalid category' }
        });
        enqueueSnackbar('Invalid category. Please select at least one valid category.', { variant: 'error' });
      } else if (message.includes('Time spent')) {
        this.setState({
          errors: { ...this.state.errors, timeSpent: 'Invalid time spent value' }
        });
        enqueueSnackbar('Invalid time spent value. Please select a valid time.', { variant: 'error' });
      } else if (message.includes('Date')) {
        this.setState({
          errors: { ...this.state.errors, interactionDate: 'Invalid date' }
        });
        enqueueSnackbar('Invalid interaction date. Please select a valid date.', { variant: 'error' });
      } else {
        enqueueSnackbar(message, { variant: 'error' });
      }
    } else if (error.networkError) {
      // Handle network errors
      enqueueSnackbar('Network error. Please check your connection and try again.', { variant: 'error' });
    } else {
      // Generic error
      enqueueSnackbar('An unexpected error occurred. Please try again.', { variant: 'error' });
    }
  };

  handleSubmit = async (createQuickNote, createQuickNotesBulk, enqueueQuickNotesBulk) => {
    if (!this.validateForm()) {
      const { enqueueSnackbar } = this.props;
      enqueueSnackbar('Please fix the errors in the form before submitting.', { variant: 'warning' });
      return;
    }

    const { selectedUsers, comment, timeSpent, activityTypeId, categoryIds, interactionDate, planned, attachments, selectedPipelineCard } = this.state;
    const { enqueueSnackbar } = this.props;

    this.setState({ isSubmitting: true });

    try {
      // Prepare base variables
      const baseVariables = {
        comment: comment.trim(),
        time_spent: parseInt(timeSpent),
        activity_type_id: activityTypeId,
        category_ids: categoryIds, // Use category_ids array instead of quick_note_category_id
        interaction_date: interactionDate.toISOString().split('T')[0],
        planned: planned,
        pipeline_card_id: selectedPipelineCard?.id || null,
      };

      // Only include attachments if there are files to upload
      if (attachments.length > 0) {
        baseVariables.attachments = attachments;
      }

      // Set context for file uploads
      const mutationContext = attachments.length > 0 ? { hasUpload: true } : {};

      // Determine which mutation to use based on selection count
      const userCount = selectedUsers.length;
      
      if (userCount === 1) {
        // Single user creation
        const selectedUser = selectedUsers[0];
        const result = await createQuickNote({
          variables: {
            ...baseVariables,
            inventory_user_id: selectedUser.id,
          },
          context: mutationContext,
        });

        if (result.data && result.data.createQuickNote) {
          enqueueSnackbar('Quick Note created successfully!', { variant: 'success' });
          this.resetForm();
          
          if (this.props.onQuickNoteCreated) {
            this.props.onQuickNoteCreated(result.data.createQuickNote);
          }
        }
      } else if (userCount <= 3) {
        // Synchronous bulk creation (2-3 users)
        const result = await createQuickNotesBulk({
          variables: {
            ...baseVariables,
            inventory_user_ids: selectedUsers.map(user => user.id),
          },
          context: mutationContext,
        });

        if (result.data && result.data.createQuickNotesBulk) {
          const { notes, count, errors } = result.data.createQuickNotesBulk;
          
          if (errors && errors.length > 0) {
            enqueueSnackbar(`Error creating notes: ${errors.join(', ')}`, { variant: 'error' });
          } else {
            enqueueSnackbar(`Successfully created ${count} Quick Notes!`, { variant: 'success' });
            this.resetForm();
            
            if (this.props.onQuickNoteCreated) {
              // Call callback for each created note
              notes.forEach(note => this.props.onQuickNoteCreated(note));
            }
          }
        }
      } else {
        // Asynchronous bulk creation (4-10 users)
        const result = await enqueueQuickNotesBulk({
          variables: {
            ...baseVariables,
            inventory_user_ids: selectedUsers.map(user => user.id),
          },
          context: mutationContext,
        });

        if (result.data && result.data.enqueueQuickNotesBulk) {
          const { count, message, errors } = result.data.enqueueQuickNotesBulk;
          
          if (errors && errors.length > 0) {
            enqueueSnackbar(`Error starting bulk creation: ${errors.join(', ')}`, { variant: 'error' });
          } else {
            enqueueSnackbar(
              message || `Bulk creation started for ${count} users. Notes will be created in the background.`, 
              { variant: 'info', persist: true }
            );
            this.resetForm();
          }
        }
      }
    } catch (error) {
      console.error('Error creating Quick Note(s):', error);
      this.handleGraphQLError(error);
    } finally {
      this.setState({ isSubmitting: false });
    }
  };

  resetForm = () => {
    this.setState({
      selectedUsers: [],
      comment: '',
      timeSpent: 30,
      activityTypeId: '',
      categoryIds: [], // Changed from categoryId to categoryIds
      interactionDate: new Date(),
      planned: false,
      attachments: [],
      selectedPipelineCard: null,
      errors: {},
    });
  };

  formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    const { enqueueSnackbar } = this.props;
    
    // Allowed file extensions
    const allowedExtensions = ['.pdf', '.png', '.jpg', '.jpeg', '.xls', '.xlsx', '.csv', '.eml', '.docx', '.one'];
    
    const validFiles = [];
    const invalidSizeFiles = [];
    const invalidTypeFiles = [];
    
    files.forEach(file => {
      const fileName = file.name.toLowerCase();
      const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));
      
      if (!hasValidExtension) {
        invalidTypeFiles.push(file.name);
      } else if (file.size > maxSize) {
        invalidSizeFiles.push(`${file.name} (${this.formatFileSize(file.size)})`);
      } else {
        validFiles.push(file);
      }
    });
    
    if (invalidTypeFiles.length > 0) {
      enqueueSnackbar(
        `The following files have invalid file types: ${invalidTypeFiles.join(', ')}. Allowed types: PDF, PNG, JPG, XLS, XLSX, CSV, EML, DOCX, ONE`,
        { variant: 'error' }
      );
    }
    
    if (invalidSizeFiles.length > 0) {
      enqueueSnackbar(
        `The following files exceed the 10MB limit: ${invalidSizeFiles.join(', ')}`,
        { variant: 'warning' }
      );
    }
    
    if (validFiles.length > 0) {
      this.setState(prevState => ({
        attachments: [...prevState.attachments, ...validFiles]
      }));
      enqueueSnackbar(`${validFiles.length} file(s) added`, { variant: 'success' });
    }
    
    // Reset the input value to allow selecting the same file again
    event.target.value = '';
  };

  handleRemoveAttachment = (index) => {
    this.setState(prevState => ({
      attachments: prevState.attachments.filter((_, i) => i !== index)
    }));
  };

  handleUserChange = (users) => {
    // Handle both single user (object) and multiple users (array) from InventoryUserAutocomplete
    const selectedUsers = Array.isArray(users) ? users : (users ? [users] : []);
    this.setState({ 
      selectedUsers, 
      errors: { ...this.state.errors, selectedUsers: null } 
    });
  };

  // Validate individual fields on blur for better UX
  handleFieldBlur = (field) => () => {
    const errors = { ...this.state.errors };
    const { selectedUsers, comment, timeSpent, activityTypeId, categoryIds, interactionDate } = this.state;

    switch (field) {
      case 'comment':
        if (!comment.trim()) {
          errors.comment = 'Comment is required';
        } else if (comment.trim().length < 5) {
          errors.comment = 'Comment must be at least 5 characters long';
        } else if (comment.trim().length > 2000) {
          errors.comment = 'Comment cannot exceed 2000 characters';
        } else {
          delete errors.comment;
        }
        break;
      
      case 'timeSpent':
        if (!timeSpent) {
          errors.timeSpent = 'Time spent is required';
        } else {
          const timeValue = parseInt(timeSpent);
          if (isNaN(timeValue) || timeValue < 15 || timeValue > 480) {
            errors.timeSpent = 'Time spent must be between 15 minutes and 8 hours';
          } else if (timeValue % 15 !== 0) {
            errors.timeSpent = 'Time spent must be in 15-minute increments';
          } else {
            delete errors.timeSpent;
          }
        }
        break;
      
      case 'activityTypeId':
        if (!activityTypeId) {
          errors.activityTypeId = 'Activity type is required';
        } else {
          delete errors.activityTypeId;
        }
        break;
      
      case 'categoryIds':
        if (!categoryIds || categoryIds.length === 0) {
          errors.categoryIds = 'At least one category is required';
        } else {
          delete errors.categoryIds;
        }
        break;
      
      case 'interactionDate':
        if (!interactionDate) {
          errors.interactionDate = 'Interaction date is required';
        } else {
          const today = new Date();
          today.setHours(23, 59, 59, 999);
          
          if (interactionDate > today) {
            errors.interactionDate = 'Interaction date cannot be in the future';
          } else {
            const oneYearAgo = new Date();
            oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
            if (interactionDate < oneYearAgo) {
              errors.interactionDate = 'Interaction date cannot be more than 1 year ago';
            } else if (isNaN(interactionDate.getTime())) {
              errors.interactionDate = 'Please enter a valid date';
            } else {
              delete errors.interactionDate;
            }
          }
        }
        break;
    }

    this.setState({ errors });
  };

  // Enhanced field change handler with validation clearing
  handleFieldChange = (field) => (event) => {
    const value = event.target.value;
    const errors = { ...this.state.errors };
    
    // Clear error for this field when user starts typing
    if (errors[field]) {
      delete errors[field];
    }
    
    this.setState({
      [field]: value,
      errors,
    });
  };

  handleDateChange = (date) => {
    const errors = { ...this.state.errors };
    
    // Clear date error when user changes date
    if (errors.interactionDate) {
      delete errors.interactionDate;
    }
    
    this.setState({
      interactionDate: date,
      errors,
    });
  };

  getSubmitButtonText = () => {
    const { selectedUsers } = this.state;
    const userCount = selectedUsers.length;

    if (userCount <= 1) {
      return 'Save Note';
    } else if (userCount <= 3) {
      return `Save ${userCount} Notes`;
    } else {
      return `Start Bulk Creation (${userCount} Notes)`;
    }
  };

  render() {
    const { classes } = this.props;
    const {
      selectedUsers,
      comment,
      timeSpent,
      activityTypeId,
      categoryIds,
      interactionDate,
      planned,
      selectedPipelineCard,
      errors,
      isSubmitting,
    } = this.state;

    return (
      <Card className={classes.card}>
        <CardContent className={classes.cardContent}>
          <Query query={GET_FORM_DATA}>
            {({ loading, error, data }) => {
              if (loading) return <CircularProgress size={24} />;
              if (error) {
                console.error('Error loading form data:', error);
                return <Typography color="error">Error loading form data</Typography>;
              }

              const activityTypes = data?.getActivityTypes || [];
              const categories = data?.getQuickNoteCategories || [];
              
              // Process categories to remove prefixes for display
              const processedCategories = categories.map(category => {
                const cleaned = category.name
                  .replace('Practice Exchange   ', '')
                  .replace('Succession Planning   ', '');
                
                // Capitalize 3-letter words (like rsp, erd, etc.)
                const displayName = cleaned.split(' ').map(word => 
                  word.length === 3 ? word.toUpperCase() : word
                ).join(' ');
                
                return {
                  ...category,
                  displayName,
                  section: category.name.startsWith('Practice Exchange   ') ? 'Practice Exchange' :
                          category.name.startsWith('Succession Planning   ') ? 'Succession Planning' : 'Other'
                };
              });
              
              // Group by section
              const practiceExchange = processedCategories.filter(c => c.section === 'Practice Exchange');
              const successionPlanning = processedCategories.filter(c => c.section === 'Succession Planning');
              const other = processedCategories.filter(c => c.section === 'Other');

              return (
                <Mutation mutation={CREATE_QUICK_NOTE}>
                  {(createQuickNote) => (
                    <Mutation mutation={CREATE_QUICK_NOTES_BULK}>
                      {(createQuickNotesBulk) => (
                        <Mutation mutation={ENQUEUE_QUICK_NOTES_BULK}>
                          {(enqueueQuickNotesBulk) => (
                            <form onSubmit={(e) => { 
                              e.preventDefault(); 
                              this.handleSubmit(createQuickNote, createQuickNotesBulk, enqueueQuickNotesBulk); 
                            }}>
                              <Grid container spacing={2}>
                                {/* User Selection */}
                                <Grid item xs={12}>
                                  <InventoryUserAutocomplete
                                    label="Select Users"
                                    value={selectedUsers}
                                    onChange={this.handleUserChange}
                                    error={!!errors.selectedUsers}
                                    helperText={errors.selectedUsers}
                                    placeholder="Search and select multiple users..."
                                    isMulti={true}
                                    maxSelections={10}
                                    showSelectionManagement={true}
                                  />
                                </Grid>

                                {/* Pipeline Card / Case Selector */}
                                <Grid item xs={12}>
                                  <Query query={GET_ACTIVE_DEALS}>
                                    {({ loading, data, error }) => (
                                      <Autocomplete
                                        options={data?.active_deals_for_quick_notes || []}
                                        getOptionLabel={(option) => {
                                          if (!option) return '';
                                          const name = option.name || 'Unnamed';
                                          const pipelineName = option.pipeline?.name || 'Unknown Pipeline';
                                          const stageName = option.pipeline_stage?.name || 'Unknown Stage';
                                          return `${name} - ${pipelineName} (${stageName})`;
                                        }}
                                        value={selectedPipelineCard}
                                        onChange={(e, value) => this.setState({ selectedPipelineCard: value })}
                                        loading={loading}
                                        renderInput={(params) => (
                                          <TextField
                                            {...params}
                                            label="Link to Case/Deal (optional)"
                                            helperText="Associate this note with an active deal or case"
                                            error={!!error}
                                            InputLabelProps={{ shrink: true }}
                                          />
                                        )}
                                      />
                                    )}
                                  </Query>
                                </Grid>

                        {/* Activity Type */}
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth error={!!errors.activityTypeId}>
                            <InputLabel>Activity Type</InputLabel>
                            <Select
                              value={activityTypeId}
                              onChange={this.handleFieldChange('activityTypeId')}
                              onBlur={this.handleFieldBlur('activityTypeId')}
                            >
                              {activityTypes.map((type) => (
                                <MenuItem key={type.id} value={type.id}>
                                  {type.name}
                                </MenuItem>
                              ))}
                            </Select>
                            {errors.activityTypeId && (
                              <FormHelperText>{errors.activityTypeId}</FormHelperText>
                            )}
                          </FormControl>
                        </Grid>

                        {/* Categories */}
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth error={!!errors.categoryIds}>
                            <InputLabel>Categories</InputLabel>
                            <Select
                              multiple
                              value={categoryIds}
                              onChange={this.handleFieldChange('categoryIds')}
                              onBlur={this.handleFieldBlur('categoryIds')}
                              renderValue={(selected) => {
                                // Get category names for selected IDs
                                const allCategories = [...practiceExchange, ...successionPlanning, ...other];
                                return selected
                                  .map(id => {
                                    const cat = allCategories.find(c => c.id === id);
                                    return cat ? cat.displayName : id;
                                  })
                                  .join(', ');
                              }}
                            >
                              {practiceExchange.length > 0 && (
                                <ListSubheader disableSticky>Practice Exchange</ListSubheader>
                              )}
                              {practiceExchange.map((category) => (
                                <MenuItem key={category.id} value={category.id}>
                                  <Checkbox checked={categoryIds.indexOf(category.id) > -1} />
                                  {category.displayName}
                                </MenuItem>
                              ))}
                              
                              {successionPlanning.length > 0 && (
                                <ListSubheader disableSticky>Succession Planning</ListSubheader>
                              )}
                              {successionPlanning.map((category) => (
                                <MenuItem key={category.id} value={category.id}>
                                  <Checkbox checked={categoryIds.indexOf(category.id) > -1} />
                                  {category.displayName}
                                </MenuItem>
                              ))}
                              
                              {other.length > 0 && (
                                <ListSubheader disableSticky>Other</ListSubheader>
                              )}
                              {other.map((category) => (
                                <MenuItem key={category.id} value={category.id}>
                                  <Checkbox checked={categoryIds.indexOf(category.id) > -1} />
                                  {category.displayName}
                                </MenuItem>
                              ))}
                            </Select>
                            {errors.categoryIds && (
                              <FormHelperText>{errors.categoryIds}</FormHelperText>
                            )}
                            <FormHelperText>You can select multiple categories</FormHelperText>
                          </FormControl>
                        </Grid>

                        {/* Comment */}
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label="Notes"
                            placeholder="Notes that will attach to the profile card"
                            value={comment}
                            onChange={this.handleCommentChange}
                            onBlur={this.handleFieldBlur('comment')}
                            error={!!errors.comment}
                            helperText={
                              errors.comment || 
                              `${comment.length}/2000 characters${comment.length < 5 ? ' (minimum 5 characters)' : ''}`
                            }
                          />
                        </Grid>

                        {/* File Attachments */}
                        <Grid item xs={12}>
                          <Typography variant="subtitle1" gutterBottom>
                            Attachments (Optional)
                          </Typography>
                          {selectedUsers.length > 1 && (
                            <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                              Note: The same attachments will be added to all selected users' Quick Notes
                            </Typography>
                          )}
                          <input
                            type="file"
                            id="dashboard-attachment-input"
                            multiple
                            style={{ display: 'none' }}
                            onChange={this.handleFileSelect}
                            accept=".pdf,.png,.jpg,.jpeg,.xls,.xlsx,.csv,.eml,.docx,.one"
                          />
                          <label htmlFor="dashboard-attachment-input">
                            <Box className={classes.fileUploadSection}>
                              <AttachFileIcon color="primary" style={{ fontSize: 40, marginBottom: 8 }} />
                              <Typography variant="body2" color="textSecondary">
                                Click to select files or drag and drop
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                Max 10MB per file · Allowed: PDF, PNG, JPG, XLS, XLSX, CSV, EML, DOCX, ONE
                              </Typography>
                            </Box>
                          </label>

                          {this.state.attachments.length > 0 && (
                            <Box className={classes.attachmentList}>
                              {this.state.attachments.map((file, index) => (
                                <Chip
                                  key={index}
                                  label={`${file.name} (${this.formatFileSize(file.size)})`}
                                  onDelete={() => this.handleRemoveAttachment(index)}
                                  deleteIcon={<DeleteIcon />}
                                  color="primary"
                                  variant="outlined"
                                  className={classes.attachmentChip}
                                />
                              ))}
                            </Box>
                          )}
                        </Grid>

                        {/* Date and Time Spent */}
                        <Grid item xs={12} sm={6}>
                          <MuiPickersUtilsProvider utils={DateFnsUtils}>
                            <KeyboardDatePicker
                              fullWidth
                              variant="inline"
                              format="MM/dd/yyyy"
                              margin="normal"
                              label="Interaction Date"
                              value={interactionDate}
                              onChange={this.handleDateChange}
                              onBlur={this.handleFieldBlur('interactionDate')}
                              maxDate={new Date()}
                              error={!!errors.interactionDate}
                              helperText={errors.interactionDate}
                              KeyboardButtonProps={{
                                'aria-label': 'change date',
                              }}
                            />
                          </MuiPickersUtilsProvider>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth error={!!errors.timeSpent} style={{ marginTop: 16 }}>
                            <InputLabel>Time Spent</InputLabel>
                            <Select
                              value={timeSpent}
                              onChange={this.handleFieldChange('timeSpent')}
                              onBlur={this.handleFieldBlur('timeSpent')}
                            >
                              {TIME_SPENT_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                            {errors.timeSpent && (
                              <FormHelperText>{errors.timeSpent}</FormHelperText>
                            )}
                          </FormControl>
                        </Grid>

                        {/* Planned/Unplanned */}
                        <Grid item xs={12}>
                          <FormControl component="fieldset" error={!!errors.planned}>
                            <Typography variant="subtitle1" gutterBottom>
                              Type
                            </Typography>
                            <RadioGroup
                              row
                              value={planned}
                              onChange={(e) => this.setState({ planned: e.target.value === 'true' })}
                            >
                              <FormControlLabel
                                value={true}
                                control={<Radio color="primary" />}
                                label="Planned"
                              />
                              <FormControlLabel
                                value={false}
                                control={<Radio color="primary" />}
                                label="Unplanned"
                              />
                            </RadioGroup>
                            {errors.planned && (
                              <FormHelperText>{errors.planned}</FormHelperText>
                            )}
                          </FormControl>
                        </Grid>

                                {/* Submit Button */}
                                <Grid item xs={12}>
                                  <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    disabled={isSubmitting}
                                    className={classes.submitButton}
                                  >
                                    {isSubmitting ? (
                                      <CircularProgress size={24} />
                                    ) : (
                                      this.getSubmitButtonText()
                                    )}
                                  </Button>
                                </Grid>

                                {/* View All Link */}
                                <Grid item xs={12} style={{ textAlign: 'center', marginTop: '16px' }}>
                                  <Typography variant="body2">
                                    <a href="/admin/quick_notes">View All Quick Notes</a>
                                  </Typography>
                                </Grid>
                              </Grid>
                            </form>
                          )}
                        </Mutation>
                      )}
                    </Mutation>
                  )}
                </Mutation>
              );
            }}
          </Query>
        </CardContent>
      </Card>
    );
  }
}

QuickNotesForm.propTypes = {
  classes: PropTypes.object.isRequired,
  enqueueSnackbar: PropTypes.func.isRequired,
  onQuickNoteCreated: PropTypes.func, // Optional callback when a Quick Note is successfully created
};

const styles = (theme) => ({
  card: {
    minHeight: 400,
    // Remove maxHeight to allow content to expand
    // maxHeight: 600,
    // overflow: 'auto',
  },
  cardContent: {
    padding: theme.spacing(2),
    paddingBottom: theme.spacing(4), // More bottom padding to ensure button is visible
  },
  submitButton: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2), // Increased bottom margin
    width: '100%', // Make button full width for better visibility
    position: 'relative', // Ensure proper positioning
    zIndex: 1, // Ensure button is above other elements
  },
  fileUploadSection: {
    border: `2px dashed ${theme.palette.primary.main}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
    textAlign: 'center',
    cursor: 'pointer',
    backgroundColor: theme.palette.action.hover,
    transition: 'background-color 0.3s',
    '&:hover': {
      backgroundColor: theme.palette.action.selected,
    },
  },
  attachmentList: {
    marginTop: theme.spacing(2),
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  attachmentChip: {
    maxWidth: 250,
    '& .MuiChip-label': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
});

export default withStyles(styles)(withSnackbar(QuickNotesForm)); 